import React, { useEffect, useState } from "react";
import { Table, Button, Form, InputGroup } from "react-bootstrap";
import { useFormik } from "formik";
import { FaPlus, FaSave, FaTrash, FaTimes } from "react-icons/fa";

import { initialValues, validationSchema } from "./sublimationSizeValidation";
import {
  handleGetSublimation_list,
  handleInputChange,
  handleSublimation_Create,
  handleSublimation_Delete,
  handleSublimation_Update,
} from "./sublimationModel";

const SublimationSizeTable = () => {
  const [sizes, setSizes] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 100,
  });
  const [loading, setLoading] = useState(false);
  const [loaderADES, setLoaderADES] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    handleGetSublimation_list({ filter, setSizes, setLoading });
  }, [filter]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      handleSublimation_Create({ setLoaderADES, sizes, setSizes, values });
      resetForm();
    },
  });

  const filteredSizes = sizes.filter(
    (row) =>
      row.tshirt_size.toLowerCase().includes(search.toLowerCase()) ||
      row.size_name.toLowerCase().includes(search.toLowerCase()) ||
      row.upper_size.toLowerCase().includes(search.toLowerCase()) ||
      String(row.size_no).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-3" style={{ fontSize: "0.85rem" }}>
      {/* Header & Search */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Sublimation Sizes</h6>
        <InputGroup style={{ width: "250px" }} size="sm">
          <Form.Control
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <Button
              variant="outline-secondary"
              onClick={() => setSearch("")}
              style={{ padding: "0 8px" }}
            >
              <FaTimes />
            </Button>
          )}
        </InputGroup>
      </div>

      {/* Scrollable Table Container */}
      <div
        style={{
          maxHeight: "400px",
          overflowY: "auto",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
        }}
      >
        <Table
          striped
          bordered
          hover
          size="sm"
          responsive
          style={{ marginBottom: 0 }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "white",
              zIndex: 2,
            }}
          >
            <tr>
              <th>T-Shirt</th>
              <th>Size Name</th>
              <th>Size No.</th>
              <th>Upper</th>
              <th style={{ width: "120px" }}>Actions</th>
            </tr>

            {/* Add New Row Inputs inside thead */}
            <tr>
              <td>
                <Form.Control
                  style={{
                    height: "28px",
                    fontSize: "0.85rem",
                    padding: "2px 8px",
                  }}
                  name="tshirt_size"
                  value={`${formik.values.tshirt_size}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.tshirt_size && formik.errors.tshirt_size
                  }
                  placeholder="T-Shirt"
                />
                <Form.Control.Feedback
                  type="invalid"
                  style={{ fontSize: "0.75rem" }}
                >
                  {formik.errors.tshirt_size}
                </Form.Control.Feedback>
              </td>
              <td>
                <Form.Control
                  style={{
                    height: "28px",
                    fontSize: "0.85rem",
                    padding: "2px 8px",
                  }}
                  name="size_name"
                  value={formik.values.size_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.size_name && formik.errors.size_name
                  }
                  placeholder="Size Name"
                />
                <Form.Control.Feedback
                  type="invalid"
                  style={{ fontSize: "0.75rem" }}
                >
                  {formik.errors.size_name}
                </Form.Control.Feedback>
              </td>
              <td>
                <Form.Control
                  style={{
                    height: "28px",
                    fontSize: "0.85rem",
                    padding: "2px 8px",
                  }}
                  name="size_no"
                  value={formik.values.size_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.size_no && formik.errors.size_no}
                  placeholder="Size No."
                  type="number"
                  maxLength={3}
                />
                <Form.Control.Feedback
                  type="invalid"
                  style={{ fontSize: "0.75rem" }}
                >
                  {formik.errors.size_no}
                </Form.Control.Feedback>
              </td>
              <td>
                <Form.Control
                  style={{
                    height: "28px",
                    fontSize: "0.85rem",
                    padding: "2px 8px",
                  }}
                  name="upper_size"
                  value={formik.values.upper_size}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.upper_size && formik.errors.upper}
                  placeholder="Upper"
                />
                <Form.Control.Feedback
                  type="invalid"
                  style={{ fontSize: "0.75rem" }}
                >
                  {formik.errors.upper_size}
                </Form.Control.Feedback>
              </td>
              <td className="text-center" style={{ verticalAlign: "middle" }}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={formik.handleSubmit}
                  style={{ padding: "4px 8px", fontSize: "0.85rem" }}
                >
                  {loaderADES?.create === true ? (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    <FaPlus />
                  )}
                </Button>
              </td>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-2">
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Loading...
                </td>
              </tr>
            ) : filteredSizes.length > 0 ? (
              filteredSizes.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Form.Control
                      style={{
                        height: "28px",
                        fontSize: "0.85rem",
                        padding: "2px 8px",
                      }}
                      value={row.tshirt_size}
                      onChange={(e) =>
                        handleInputChange(
                          row.id,
                          "tshirt_size",
                          e.target.value,
                          setSizes,
                          sizes
                        )
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      style={{
                        height: "28px",
                        fontSize: "0.85rem",
                        padding: "2px 8px",
                      }}
                      value={row.size_name}
                      onChange={(e) =>
                        handleInputChange(
                          row.id,
                          "size_name",
                          e.target.value,
                          setSizes,
                          sizes
                        )
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      style={{
                        height: "28px",
                        fontSize: "0.85rem",
                        padding: "2px 8px",
                      }}
                      value={row.size_no}
                      onChange={(e) =>
                        handleInputChange(
                          row.id,
                          "size_no",
                          e.target.value,
                          setSizes,
                          sizes
                        )
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      style={{
                        height: "28px",
                        fontSize: "0.85rem",
                        padding: "2px 8px",
                      }}
                      value={row.upper_size}
                      onChange={(e) =>
                        handleInputChange(
                          row.id,
                          "upper_size",
                          e.target.value,
                          setSizes,
                          sizes
                        )
                      }
                    />
                  </td>
                  <td className="d-flex justify-content-between">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() =>
                        handleSublimation_Update({ item: row, setLoaderADES })
                      }
                      disabled={loaderADES?.update === row.id}
                    >
                      {loaderADES?.update === row.id ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        </>
                      ) : (
                        <FaSave />
                      )}
                    </Button>

                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() =>
                        handleSublimation_Delete({
                          setLoaderADES,
                          setSizes,
                          id: row.id,
                        })
                      }
                      disabled={loaderADES?.delete === row.id}
                    >
                      {loaderADES?.delete === row.id ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        </>
                      ) : (
                        <FaTrash />
                      )}
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted py-2">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default SublimationSizeTable;
